// src/services/buildUrl.js
//
// ROUTING-PHASE-1: URL builder helpers.
// Canonical mapping from legacy view-enum strings to URL paths, and
// convenience constructors for deep-links into specific pages.
// Replaces ad-hoc pushNavState({ view, projectId, … }) call-sites.

// Auxiliary protected tabs retain their legacy namespace; the three public
// market lenses are composed inside Places.
const MARKET_PLAN_TAB_TO_SEGMENT = {
  strategy:        "strategy",
  plan:            "plan",
  market_pressure: "pressure",
  whatif:          "scenario",
  recommendations: "guidance",
};

/**
 * Build the canonical URL for a Market Plan tab.
 * The Plan tab is Places → Markets. Pressure and Scenario are sibling lenses.
 * Market record links open the Markets drawer; Capacity keeps its own contract.
 */
export function buildMarketPlanUrl(tab, marketId) {
  const segment = MARKET_PLAN_TAB_TO_SEGMENT[tab] ?? "plan";
  // PLACES-CAPACITY-UNIFIED: Pressure and Scenario collapse into Capacity;
  // Scenario links open its panel, and a market is expanded via `open`.
  if (segment === "pressure" || segment === "scenario") {
    const params = new URLSearchParams();
    if (segment === "scenario") params.set("panel", "scenario");
    if (marketId) params.set("open", marketId);
    const q = params.toString();
    return `/places/capacity${q ? `?${q}` : ""}`;
  }
  const base = segment === "plan" ? "/places/markets" : `/markets/${segment}`;
  return marketId ? `${base}?${segment === "plan" ? "peek" : "expandMarket"}=${encodeURIComponent(marketId)}` : base;
}

// Canonical landing URL for the inline-expanded Projects list.
export function buildProjectsUrl(locationId = null) {
  return locationId
    ? `/projects?locationId=${encodeURIComponent(locationId)}`
    : "/projects";
}

export const PROJECT_TAB_SEGMENTS = Object.freeze(["portfolio", "weekly", "gantt", "travel", "financials", "risk-register", "participants", "team", "signals", "contracts"]);

export const TRAVEL_QUERY_KEYS = Object.freeze(["month", "tz", "travelProject", "travelPerson", "travelMarket", "travelPurpose"]);

export function projectTabFromPath(pathname) {
  const segment = /^\/projects\/([^/]+)\/?$/.exec(pathname || "")?.[1];
  if (segment === "team") return "participants";
  return PROJECT_TAB_SEGMENTS.includes(segment) ? segment : null;
}

// Preserve supported query/filter contracts when changing tabs or Weekly state.
export function buildProjectsTabUrl(tab, search = "", patch = {}) {
  const params = new URLSearchParams(search);
  for (const [key, value] of Object.entries(patch)) {
    if (value == null) params.delete(key);
    else params.set(key, value);
  }
  if (tab !== "risk-register") params.delete("riskProject");
  // Travel URL state belongs to the Travel lens only.
  if (tab !== "travel") for (const key of TRAVEL_QUERY_KEYS) params.delete(key);
  const view = params.get("view");
  if (tab === "travel" ? view && view !== "month" && view !== "list" : view === "month" || view === "list") params.delete("view");
  const query = params.toString();
  return `/projects/${tab === "participants" ? "team" : PROJECT_TAB_SEGMENTS.includes(tab) ? tab : "portfolio"}${query ? `?${query}` : ""}`;
}

export function buildProjectRiskRegisterUrl(projectId) {
  return buildProjectsTabUrl("risk-register", "", { riskProject: projectId });
}

// Reject rolled-over dates and non-Mondays; never normalize an invalid link
// into an invented week. Week keys follow the existing UTC calendar policy.
export function validProjectWeek(value) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value || "")) return null;
  const date = new Date(`${value}T00:00:00Z`);
  return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === value && date.getUTCDay() === 1
    ? value : null;
}

export function validWeeklyView(value) {
  if (value === "table") return "grid";
  return ["agenda", "grid"].includes(value) ? value : null;
}

// Map every legacy VIEW_* constant to its canonical URL path.
export const VIEW_TO_PATH = {
  places:                    "/places",
  home:                      "/home",
  portfolio:                 "/places/offices",
  leases:                    "/leases",
  plan:                      "/places/markets",
  projects:                  "/projects",
  okrs:                      "/okrs",
  "reports-landing":         "/reports",
  "report-wpr":              "/reports/wpr",
  "report-headcount-trend":  "/reports/headcount-trend",
  "report-open-roles":       "/reports/open-roles",
  "report-rto-compliance":   "/reports/rto-compliance",
  "report-foot-traffic":     "/reports/foot-traffic",
  "report-space-fit":        "/reports/space-fit",
  "report-cost-leakage":     "/reports/cost-leakage",
  "report-utilization":      "/reports/utilization",
  "report-data-coverage":    "/reports/data-coverage",
  "currency-rates":          "/currency-rates",
  admin:                     "/admin",
  "imports-queue":           "/admin/data/imports",
  "imports-session":         "/admin/data/import",
  // NAV-RESTRUCTURE-RADAR: "radar" nav key targets /infocus-radar; the old
  // /radar (Leadership Radar) stays accessible via the route but is no longer
  // a nav item. "infocus-radar" view key still resolves directly.
  radar:                     "/infocus-radar",
  "infocus-radar":           "/infocus-radar",
  playbooks:                 "/playbooks",
  decisions:                 "/radar/decisions",
  reviews:                   "/reviews",
  roadmap:                   "/roadmap",
  workflows:                 "/workflows",
  "start-here":             "/start-here",
  "data_health":             "/admin",
  "location-detail":         "/places/offices",
  "project-detail":          "/projects",
  "lease-detail":            "/leases",
};

/**
 * Convert a legacy view-enum string to the canonical URL path.
 * Falls back to the Offices lens for unknown view keys.
 */
export function viewToPath(viewKey) {
  return VIEW_TO_PATH[viewKey] ?? "/places/offices";
}

/**
 * Map a URL pathname to the NavRail active-view key.
 * Returns null when no match is found (callers can fall back to a prop).
 */
export function pathToNavView(pathname) {
  if (!pathname) return null;
  if (/^\/(?:places|offices|markets|market-plan)(?:\/|$)/.test(pathname)) return "places";
  if (pathname.startsWith("/home"))         return "home";
  if (pathname.startsWith("/offices"))      return "portfolio";
  if (pathname.startsWith("/leases"))       return "leases";
  // NAV-RESTRUCTURE-RADAR: both /infocus-radar and /radar highlight "radar" item.
  if (pathname.startsWith("/infocus-radar")) return "radar";
  if (pathname.startsWith("/radar"))         return "radar";
  if (pathname.startsWith("/playbooks"))     return "playbooks";
  if (pathname.startsWith("/decisions"))    return "radar";
  if (pathname.startsWith("/reviews"))      return "reviews";
  if (pathname.startsWith("/markets"))      return "plan";
  if (pathname.startsWith("/projects"))     return "projects";
  if (pathname.startsWith("/okrs"))         return "okrs";
  if (pathname.startsWith("/reports"))      return "reports-landing";
  if (pathname.startsWith("/admin") || pathname.startsWith("/imports")) return "admin";
  if (pathname.startsWith("/roadmap"))      return "roadmap";
  if (pathname.startsWith("/workflows"))    return "workflows";
  if (pathname.startsWith("/start-here"))  return "start-here";
  return null;
}

/**
 * Compute the back-button label from a returnPath string.
 */
export function returnPathToLabel(returnPath) {
  if (returnPath?.startsWith("/places/offices")) return "Offices";
  if (returnPath?.startsWith("/places/")) return "Places";
  if (!returnPath || returnPath === "/offices")             return "Offices";
  if (returnPath === "/leases")                             return "Leases";
  if (returnPath === "/home")                               return "Home";
  if (returnPath === "/reports")                            return "Reports";
  if (returnPath.startsWith("/reports"))                    return "Reports";
  if (returnPath === "/workflows")                          return "Actions";
  // PLACES-SLICE-2: a market record (/markets/<id>[/tab]) returns as "Market".
  if (/^\/markets\/(?!plan$|strategy$|pressure$|scenario$|guidance$)[^/]+(?:\/[^/]+)?$/.test(returnPath)) return "Market";
  if (returnPath === "/markets" || returnPath.startsWith("/markets/")) return "Market Plan";
  return "Offices";
}

/** Build a deep-link URL for a specific market detail page. */
export function buildMarketUrl(marketId) {
  return marketId ? `/places/markets?peek=${encodeURIComponent(marketId)}` : "/places/markets";
}

/** Build a deep-link URL for a specific lease detail page. */
export function buildLeaseUrl(leaseId, tab = "overview", search = "") {
  if (!leaseId) return "/leases";
  if (tab === "documents" || tab === "intel") return buildLeaseFullUrl(leaseId, tab, search);
  const params = new URLSearchParams(search);
  params.delete("peek");
  params.set("tab", LEASE_DRAWER_TABS.includes(tab) ? tab : "overview");
  return `/leases/${encodeURIComponent(leaseId)}?${params}`;
}

export const LEASE_DRAWER_TABS = Object.freeze(["overview", "terms", "financials", "dates"]);

export function buildLeaseFullUrl(leaseId, tab = "overview", search = "") {
  if (!leaseId) return "/leases";
  const params = new URLSearchParams(search);
  params.delete("peek");
  params.delete("tab");
  // Terms is a drawer-only split of Overview (no full-reader Terms tab by DB
  // design), so it opens the full reader's Overview.
  if (tab === "terms") tab = "overview";
  const suffix = tab && tab !== "overview" ? `/${encodeURIComponent(tab)}` : "";
  return `/leases/${encodeURIComponent(leaseId)}/full${suffix}${params.size ? `?${params}` : ""}`;
}

export function leaseRecordFromPath(pathname) {
  const match = /^\/leases\/([^/]+)\/?$/.exec(pathname);
  if (!match || match[1] === "timeline") return null;
  try { return decodeURIComponent(match[1]); } catch { return match[1]; }
}

// ─── Office record drawer / secondary full reader (Task #9085) ──────────────
//
// Canonical office destination is the record drawer over the Offices list:
//   /places/offices/{locationId}[?tab=space|lease|projects]
// The secondary full reader (today's LocationDetail) lives at
//   /places/offices/{locationId}/full[/{fullTab}]
export const OFFICE_DRAWER_TABS = Object.freeze(["overview", "space", "lease", "projects", "media"]);
// Full-page tab keys (LocationDetail internal keys) that only exist in the reader.
export const OFFICE_FULL_TABS = Object.freeze(["building", "cost", "utilization", "people", "service", "intel"]);

export function normalizeOfficeDrawerTab(tab) {
  return OFFICE_DRAWER_TABS.includes(tab) ? tab : "overview";
}

/** Drawer URL. Extra `search` (list context) is preserved; `tab` is owned here. */
export function buildOfficeDrawerUrl(locationId, tab = null, search = "") {
  if (!locationId) return "/places/offices";
  const params = new URLSearchParams(search);
  params.delete("tab");
  params.delete("peek");
  const t = normalizeOfficeDrawerTab(tab);
  if (t !== "overview") params.set("tab", t);
  const q = params.toString();
  return `/places/offices/${encodeURIComponent(locationId)}${q ? `?${q}` : ""}`;
}

/** Secondary full reader URL (LocationDetail). Default tab omitted. */
export function buildOfficeFullUrl(locationId, fullTab = null) {
  if (!locationId) return "/places/offices";
  const base = `/places/offices/${encodeURIComponent(locationId)}/full`;
  return fullTab && fullTab !== "building" && OFFICE_FULL_TABS.includes(fullTab) ? `${base}/${fullTab}` : base;
}

/**
 * Legacy office tab names (old /offices/:id/:tab and initTab state) resolve
 * to a supported destination. Retired lease/cost map to the drawer Lease tab;
 * reader-only tabs keep their reader; unknown names fall back to Overview —
 * an old name is never treated as an arbitrary drawer tab.
 */
export function legacyOfficeDestination(locationId, legacyTab = null, search = "") {
  if (!locationId) return "/places/offices";
  if (legacyTab === "lease" || legacyTab === "cost") return buildOfficeDrawerUrl(locationId, "lease", search);
  if (OFFICE_DRAWER_TABS.includes(legacyTab)) return buildOfficeDrawerUrl(locationId, legacyTab, search);
  if (legacyTab && legacyTab !== "building" && OFFICE_FULL_TABS.includes(legacyTab)) return buildOfficeFullUrl(locationId, legacyTab);
  return buildOfficeDrawerUrl(locationId, null, search);
}

/** Generic office record link: the drawer (legacy tab names mapped safely). */
export function buildLocationUrl(locationId, tab = null) {
  return legacyOfficeDestination(locationId, tab);
}

// ─── Project record drawer / Updates reader (Task #9085) ───────────────────
//
// /projects/{code or id}[?tab=]   → Projects list + record drawer
// /projects/updates/{id}          → existing weekly Updates reader
// Reserved collection segments are never interpreted as project identity.
export const PROJECT_RESERVED_SEGMENTS = Object.freeze([...PROJECT_TAB_SEGMENTS, "updates", "new", "full"]);
export const PROJECT_DRAWER_TABS = Object.freeze(["overview", "timeline", "financials", "media"]);

export function isReservedProjectSegment(segment) {
  return PROJECT_RESERVED_SEGMENTS.includes(segment);
}

/** Project ref from a drawer pathname, or null for reserved/collection paths. */
export function projectRefFromPath(pathname) {
  const m = /^\/projects\/([^/]+)\/?$/.exec(pathname || "");
  if (!m) return null;
  let seg;
  try { seg = decodeURIComponent(m[1]); } catch { return null; }
  return seg && !isReservedProjectSegment(seg) ? seg : null;
}

/** Updates-reader project ID from /projects/updates/{id}, else null. */
export function projectUpdatesIdFromPath(pathname) {
  const m = /^\/projects\/updates\/([^/]+)\/?$/.exec(pathname || "");
  if (!m) return null;
  try { return decodeURIComponent(m[1]) || null; } catch { return null; }
}

/** Resolve a drawer ref against existing client rows: assigned code first, then ID. */
export function resolveProjectRef(ref, projects = []) {
  if (!ref || !Array.isArray(projects)) return null;
  const byCode = projects.find((p) => typeof p?.connections?.code === "string" && p.connections.code.trim() === ref);
  return byCode ?? projects.find((p) => p?.id === ref) ?? null;
}

/** Preferred canonical identity segment: the assigned code, else the ID. */
export function projectCanonicalRef(project) {
  const code = typeof project?.connections?.code === "string" ? project.connections.code.trim() : "";
  return code && !isReservedProjectSegment(code) && !code.includes("/") ? code : project?.id ?? null;
}

/**
 * Generic project record link → the Projects record drawer. `search` may
 * carry office scope (locationId) and an explicit drawer tab only; no reader
 * or selection parameters are forwarded.
 */
export function buildProjectUrl(projectRef, search = "") {
  if (!projectRef) return "/projects";
  const source = new URLSearchParams(search);
  const params = new URLSearchParams();
  const office = source.get("locationId");
  if (office) params.set("locationId", office);
  const tab = source.get("tab");
  if (PROJECT_DRAWER_TABS.includes(tab) && tab !== "overview") params.set("tab", tab);
  const q = params.toString();
  return `/projects/${encodeURIComponent(projectRef)}${q ? `?${q}` : ""}`;
}

/** Explicit secondary reader. The drawer's source/collection stays in state. */
export function buildProjectFullUrl(projectId, search = "") {
  if (!projectId) return "/projects";
  const params = new URLSearchParams(search);
  return `/projects/${encodeURIComponent(projectId)}/full${params.size ? `?${params}` : ""}`;
}

/**
 * Explicit Updates reader link. Only week and office context belong to this
 * destination; old presentation/filter/selection parameters cannot override
 * the requested project.
 */
export function buildProjectUpdatesUrl(projectId, search = "") {
  if (!projectId) return "/projects/weekly";
  const source = new URLSearchParams(search);
  const params = new URLSearchParams();
  const week = validProjectWeek(source.get("week")) || validProjectWeek(source.get("weeklyWeek"));
  if (week) params.set("week", week);
  const view = validWeeklyView(source.get("view"));
  if (view) params.set("view", view);
  const office = source.get("locationId");
  if (office) params.set("locationId", office);
  const q = params.toString();
  return `/projects/updates/${encodeURIComponent(projectId)}${q ? `?${q}` : ""}`;
}

/**
 * Weekly-lens URL after a state patch. A selected project lives in the
 * canonical reader path; no selection stays on /projects/weekly.
 */
export function buildWeeklyStateUrl(search = "", patch = {}, currentReaderId = null) {
  const params = new URLSearchParams(search);
  if (currentReaderId && !params.has("weeklyProject")) params.set("weeklyProject", currentReaderId);
  for (const [key, value] of Object.entries(patch)) {
    if (value == null) params.delete(key);
    else params.set(key, value);
  }
  params.delete("riskProject");
  params.delete("tab");
  const project = params.get("weeklyProject");
  params.delete("weeklyProject");
  const q = params.toString();
  return project
    ? `/projects/updates/${encodeURIComponent(project)}${q ? `?${q}` : ""}`
    : `/projects/weekly${q ? `?${q}` : ""}`;
}

/** Build a deep-link URL for a specific import session. */
export function buildImportUrl(sessionId) {
  return sessionId ? `/admin/data/import/${sessionId}` : "/admin/data/imports";
}

/**
 * Build the canonical URL for the Leases page.
 * Portfolio retains the internal "list" key; Timeline has its own route.
 */
export function buildLeasesUrl(view) {
  if (!view || view === "list") return "/leases";
  if (view === "timeline") return "/leases/timeline";
  return "/leases";
}

export function isLeasesLandingPath(pathname) {
  return /^\/leases(?:\/timeline)?\/?$/.test(pathname);
}

// Own only the legacy presentation parameter, never unrelated bookmark context.
export function canonicalLeasesLocation(location) {
  const params = new URLSearchParams(location.search);
  // Explicit full-reader destinations never pass through drawer compatibility.
  if (/^\/leases\/[^/]+\/full(?:\/[^/]+)?\/?$/.test(location.pathname)) {
    return location.pathname + location.search + (location.hash || "");
  }
  const record = /^\/leases\/([^/]+)(?:\/([^/]+))?\/?$/.exec(location.pathname);
  const peek = params.get("peek");
  if ((record && record[1] !== "timeline") || peek) {
    let id = peek || record[1];
    if (!peek) { try { id = decodeURIComponent(id); } catch { /* retain opaque identity */ } }
    const legacyTab = record?.[2];
    const tab = legacyTab || params.get("tab") || "overview";
    if (params.get("view") === "timeline" || location.pathname === "/leases/timeline") params.set("collection", "timeline");
    params.delete("view");
    return buildLeaseUrl(id, tab, params) + (location.hash || "");
  }
  const timeline = /^\/leases\/timeline\/?$/.test(location.pathname)
    || params.get("view") === "timeline"
    || (params.get("view") !== "list" && params.has("focus") && !location.state?.leasesPortfolio);
  params.delete("view");
  return buildLeasesUrl(timeline ? "timeline" : "list")
    + (params.size ? `?${params}` : "") + (location.hash || "");
}
